import React from 'react';
import I18n from 'i18n-js';
import * as elog from '@reverbdotcom/commons/src/elog';
import { formatBid, DELETE_BID_AMOUNT, recommendationAmount as getRecommendationAmount, getInsight } from './bump_helpers';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { Core_Bump_Recommendation_ListingQuery, CoreBumpRecommendationListingCard } from '@reverbdotcom/commons/src/gql/graphql';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { gql } from '@reverbdotcom/commons/src/gql';
import { BUMPED_LISTING_RECOMMENDATION_QUERY } from './bump_listing_query';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { useSendNativeMessage } from '@reverbdotcom/commons/src/nativeMobileBridge';
import { inMobileApp } from '@reverbdotcom/commons/src/user_context_helpers';
import type { Insight, RecommendationDetails } from '../listings_management/sellerRecommendationTracking';

export interface RecommendationSource {
  insight: Insight;
  componentName: string;
  details?: RecommendationDetails;
}

const COMPONENT_NAME = 'BumpController';

export interface BumpControllerProps {
  listingId: string;
  bid: number;
  componentName?: string;
  showModal?: boolean;
  setShowModal?: (showModal) => void;
  hidePercentCallout?: boolean;
  buttonText?: string | React.ReactNode | JSX.Element;
  buttonClassName?: string;
  refetch?: (newBid: number | null) => void;
  onModalClose?: () => void;
  disabled?: boolean;
  minimumBumpBid?: number;
  useFeaturedListingInsightPreview?: boolean;
  // When provided, the controller uses this listing instead of running its own
  // query (e.g. the seller dashboard fetches all cards' bump data in one query).
  listing?: CoreBumpRecommendationListingCard.Fragment;
  source?: RecommendationSource;
}

export interface WrappedBumpComponentProps {
  listingId: string;
  bid: number;
  selectedBid: number;
  successMessage: string;
  failMessage: string;
  handleBidChange: (bid: number) => void;
  updateBid: (bid: number, bumpRecommendationAmount: number, listing: CoreBumpRecommendationListingCard.Fragment, refetch: (newBid: number | null) => void) => void;
  listing?: CoreBumpRecommendationListingCard.Fragment;
  bumpRecommendationAmount: number | null;
  listingLoading: boolean;
  showModal: boolean;
  openModal: () => void;
  closeModal: () => void;
  showCustomWarning: boolean;
  setShowCustomWarning: (show: boolean) => void;
  onSubmit: () => void;
  onNoThankYou: () => void;
  hidePercentCallout?: boolean;
  buttonText?: string | React.ReactNode | JSX.Element;
  buttonClassName?: string;
  disabled?: boolean;
  minimumBumpBid?: number;
  showTopPickVariant?: boolean;
  source?: RecommendationSource;
}

export function trackAdjustBumpRate(
  newBid: number,
  startingBid: number,
  bumpRecommendationAmount: number,
  listing: CoreBumpRecommendationListingCard.Fragment,
  componentName: string,
) {
  trackEvent({
    componentName,
    eventName: MParticleEventName.AdjustBumpRate,
    listing,
    startingBumpRatePercent: formatBid(startingBid),
    newBumpRatePercent: formatBid(newBid),
    bumpRecommendationPercent: bumpRecommendationAmount,
  });
}

const CREATE_PRODUCT_BUMP_RATE_MUTATION = gql(`
  mutation Core_CreateProductBumpRate($listingId: String!, $bid: Float!) {
    createProductBumpRate(input: { listingId: $listingId, bid: $bid }) {
      id
      rate
    }
  }
`);

const DELETE_PRODUCT_BUMP_RATE_MUTATION = gql(`
  mutation Core_DeleteProductBumpRate($listingId: String!) {
    deleteProductBumpRate(input: { listingId: $listingId }) {
      productBumpRates {
        id
        rate
      }
    }
  }
`);

export function bumpRecommendationsController(WrappedComponent: React.ComponentType<WrappedBumpComponentProps>) {
  return function BumpController(props: BumpControllerProps) {
    const hasRequiredBumpData = Boolean(
      props.listing
      && props.listing.bumpRecommendations !== undefined
      && (
        props.listing.featuredListingInsight !== undefined
        || props.listing.featuredListingInsightPreview !== undefined
      ),
    );

    const { data: listingData, loading: queryLoading } = useQuery<Core_Bump_Recommendation_ListingQuery>(
      BUMPED_LISTING_RECOMMENDATION_QUERY,
      {
        skip: hasRequiredBumpData,
        variables: {
          id: props.listingId,
          useFeaturedListingInsightPreview: props.useFeaturedListingInsightPreview ?? false,
        },
      },
    );

    const user = useUser();

    const listing = hasRequiredBumpData ? props.listing : (listingData?.listing ?? props.listing);
    const listingLoading = hasRequiredBumpData ? false : queryLoading;
    const insight = getInsight(listing);
    const showTopPickVariant = !!insight?.eligibleForTopSpot || !!insight?.featuredWinner;
    const bumpRecommendationAmount = listing
      ? getRecommendationAmount(listing, showTopPickVariant)
      : null;

    // Bid state
    const [bid, setBid] = React.useState(props.bid);
    const [selectedBid, setSelectedBid] = React.useState(props.bid || 0);
    const [successMessage, setSuccessMessage] = React.useState('');
    const [failMessage, setFailMessage] = React.useState('');

    // Modal + UI state
    const [showModal, setShowModal] = React.useState(props.showModal ?? false);
    const [showCustomWarning, setShowCustomWarning] = React.useState(false);

    // Sync with external showModal prop
    React.useEffect(() => {
      if (props.showModal !== undefined) {
        setShowModal(props.showModal);
      }
    }, [props.showModal]);

    // Reset selectedBid when modal opens
    const [prevShowModal, setPrevShowModal] = React.useState(showModal);
    if (showModal && !prevShowModal) {
      setPrevShowModal(true);
      setSelectedBid(bid);
    } else if (!showModal && prevShowModal) {
      setPrevShowModal(false);
    }

    const openModal = React.useCallback(() => {
      setShowModal(true);
      props.setShowModal?.(true);
    }, [props.setShowModal]);

    const closeModal = React.useCallback(() => {
      setShowModal(false);
      setShowCustomWarning(false);
      props.setShowModal?.(false);
      props.onModalClose?.();
    }, [props.setShowModal, props.onModalClose]);

    // Close modal on successful bid
    const [bidSubmitted, setBidSubmitted] = React.useState(false);
    const sendNativeMessage = useSendNativeMessage();
    const inMobile = inMobileApp(user);

    React.useEffect(() => {
      if (successMessage && bidSubmitted) {
        closeModal();
        setBidSubmitted(false);
      }
    }, [successMessage, bidSubmitted, closeModal]);

    // Mutations
    const [createProductBumpRateMutation] = useMutation(CREATE_PRODUCT_BUMP_RATE_MUTATION);
    const [deleteProductBumpRateMutation] = useMutation(DELETE_PRODUCT_BUMP_RATE_MUTATION);

    const { listingId } = props;

    React.useEffect(() => {
      setBid(props.bid);
      setSelectedBid(props.bid || 0);
    }, [props.bid]);

    const coreSetBid = async (
      newBid: number,
      bumpRecommendationAmount: number,
      listing: CoreBumpRecommendationListingCard.Fragment,
      refetch: (newBid: number | null) => void,
    ): Promise<void> => {
      const result = await createProductBumpRateMutation({
        variables: {
          listingId,
          bid: newBid,
        },
      });

      if (result.errors?.length > 0) {
        elog.error('Bump Create Bid Error', { bid: newBid }, result.errors.map((error) => error.message).join(', '));
        updateErrorState();
        return;
      }

      trackAdjustBumpRate(
        newBid,
        bid,
        bumpRecommendationAmount,
        listing,
        props.componentName || COMPONENT_NAME,
      );
      updateSuccessState(newBid, refetch);
    };

    const deleteBid = async (
      bumpRecommendationAmount: number,
      listing: CoreBumpRecommendationListingCard.Fragment,
      refetch: (newBid: number | null) => void,
    ): Promise<void> => {
      const result = await deleteProductBumpRateMutation({
        variables: {
          listingId,
        },
      });

      if (result.errors?.length > 0) {
        elog.error('Bump Delete Bid Error', {}, result.errors.map((error) => error.message).join(', '));
        updateErrorState();
        return;
      }

      trackAdjustBumpRate(
        DELETE_BID_AMOUNT,
        bid,
        bumpRecommendationAmount,
        listing,
        props.componentName || COMPONENT_NAME,
      );
      updateSuccessState(null, refetch);
    };

    const updateBid = async (
      newBid: number,
      bumpRecommendationAmount: number,
      listing: CoreBumpRecommendationListingCard.Fragment,
      refetch: (newBid: number | null) => void,
    ): Promise<void> => {
      if (newBid === DELETE_BID_AMOUNT) {
        await deleteBid(
          bumpRecommendationAmount,
          listing,
          refetch,
        );
      } else {
        await coreSetBid(
          newBid,
          bumpRecommendationAmount,
          listing,
          refetch,
        );
      }
    };

    const updateSuccessState = (
      newBid: (number | null),
      refetch: (newBid: number | null) => void,
    ) => {
      const appliedBid = newBid ?? 0;

      // Sent once the mutation has succeeded so the native apps log the rate
      // that was actually applied, not the one the seller selected.
      if (inMobile) {
        sendNativeMessage({
          eventName: 'bump_bid_successful_event',
          data: {
            success: true,
            isBumped: appliedBid > 0,
            bumpRatePercent: formatBid(appliedBid),
          },
        });
      }

      setBid(newBid);
      setSelectedBid(newBid);
      if (newBid === null || newBid === 0) {
        setSuccessMessage(I18n.t('discovery.bump.successBidDelete'));
      } else {
        setSuccessMessage(I18n.t('discovery.bump.successBid', { percent: formatBid(newBid) }));
      }
      setFailMessage('');
      refetch(newBid);
    };

    const updateErrorState = () => {
      setSelectedBid(bid);
      setFailMessage(I18n.t('discovery.bump.errorBid'));
      setSuccessMessage('');
    };

    // Pre-wired submit actions
    function submitBid(bidValue: number) {
      if (!listing || bumpRecommendationAmount === null) return;
      updateBid(bidValue, bumpRecommendationAmount, listing, props.refetch ?? (() => { }));
      setBidSubmitted(true);
    }

    function onSubmit() {
      submitBid(selectedBid);
    }

    function onNoThankYou() {
      submitBid(0);
    }

    return (
      <WrappedComponent
        listingId={listingId}
        bid={bid}
        selectedBid={selectedBid}
        successMessage={successMessage}
        failMessage={failMessage}
        handleBidChange={setSelectedBid}
        updateBid={updateBid}
        listing={listing}
        bumpRecommendationAmount={bumpRecommendationAmount}
        listingLoading={listingLoading}
        showModal={showModal}
        openModal={openModal}
        closeModal={closeModal}
        showCustomWarning={showCustomWarning}
        setShowCustomWarning={setShowCustomWarning}
        onSubmit={onSubmit}
        onNoThankYou={onNoThankYou}
        hidePercentCallout={props.hidePercentCallout}
        buttonText={props.buttonText}
        buttonClassName={props.buttonClassName}
        disabled={props.disabled}
        minimumBumpBid={props.minimumBumpBid}
        showTopPickVariant={showTopPickVariant}
        source={props.source}
      />
    );
  };
}
